import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { ShieldCheck, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { PageHeader } from '../components/common/PageHeader';
import { employeeService } from '../services/employee.service';
import { selectUser } from '../store/authSlice';
import { KEYS } from '../lib/queryKeys';
import { formatINR, getPTDeduction, getPTRule, INDIAN_STATES } from '../lib/format';
import apiClient from '../services/apiClient'; // <-- import direct

export default function Compliance() {
  const qc   = useQueryClient();
  const user = useSelector(selectUser);
  const [ptState, setPtState] = useState(user?.state || 'Maharashtra');

  // ── Fetch active employees ────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: KEYS.employees({ status: 'active' }),
    queryFn:  () => employeeService.list({ status: 'active' }),
  });
  const employees = data?.data || [];

  // ── Toggle PF ─────────────────────────────────────────────────────────────
  const pfMut = useMutation({
    mutationFn: async ({ id, val }) => {
      const payload = { pfApplicable: val };
      console.log('🔵 [Frontend] PF payload:', payload);
      const response = await apiClient.patch(`/api/employees/${id}`, payload);
      return response.data;
    },
    onSuccess: (_, vars) => {
      toast.success(`PF ${vars.val ? 'enabled' : 'disabled'}`);
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  // ── Toggle ESI ────────────────────────────────────────────────────────────
  const esiMut = useMutation({
    mutationFn: async ({ id, val }) => {
      const payload = { esiApplicable: val };
      console.log('🔵 [Frontend] ESI payload:', payload);
      const response = await apiClient.patch(`/api/employees/${id}`, payload);
      return response.data;
    },
    onSuccess: (_, vars) => {
      toast.success(`ESI ${vars.val ? 'enabled' : 'disabled'}`);
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  // ── Toggle PT ─────────────────────────────────────────────────────────────
  const ptMut = useMutation({
    mutationFn: async ({ id, val }) => {
      const payload = { ptApplicable: val };
      console.log('🔵 [Frontend] PT payload:', payload);
      const response = await apiClient.patch(`/api/employees/${id}`, payload);
      return response.data;
    },
    onSuccess: (_, vars) => {
      toast.success(`PT ${vars.val ? 'enabled' : 'disabled'}`);
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const pfCount  = employees.filter(e => e.pfApplicable).length;
  const esiCount = employees.filter(e => e.esiApplicable).length;
  const ptCount  = employees.filter(e => e.ptApplicable).length;
  const total    = employees.length || 1;

  return (
    <div>
      <PageHeader title="Compliance" description="Manage PF, ESI, and Professional Tax (PT) configurations." />

      {/* Top cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        {/* PT State selector */}
        <Card className="lg:col-span-1 p-5 sm:p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-[#2E6DA4]/10 text-[#2E6DA4] flex items-center justify-center">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 font-heading">Professional Tax</div>
              <div className="text-xs text-slate-500">State-wise PT rules</div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">State</Label>
              <Select value={ptState} onValueChange={setPtState}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">PT Rule</div>
              <div className="text-sm font-medium text-slate-900 mt-1">{getPTRule(ptState)}</div>
            </div>
          </div>
        </Card>

        {/* PF stat */}
        <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">PF Enrolled</div>
          <div className="mt-3 text-3xl font-semibold font-mono text-[#2E6DA4]">
            {isLoading ? '…' : `${pfCount}/${employees.length}`}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {isLoading ? '' : `${Math.round((pfCount / total) * 100)}% coverage`}
          </div>
        </Card>

        {/* ESI stat */}
        <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">ESI Enrolled</div>
          <div className="mt-3 text-3xl font-semibold font-mono text-emerald-600">
            {isLoading ? '…' : `${esiCount}/${employees.length}`}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {isLoading ? '' : `${Math.round((esiCount / total) * 100)}% coverage`}
          </div>
        </Card>

        {/* PT stat */}
        <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">PT Enrolled</div>
          <div className="mt-3 text-3xl font-semibold font-mono text-amber-600">
            {isLoading ? '…' : `${ptCount}/${employees.length}`}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {isLoading ? '' : `${Math.round((ptCount / total) * 100)}% coverage`}
          </div>
        </Card>
      </div>

      {/* Compliance table */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 p-5 sm:p-6 border-b border-slate-200">
          <ShieldCheck className="w-5 h-5 text-[#2E6DA4]" />
          <div>
            <h3 className="text-base font-semibold text-slate-900 font-heading">Statutory deductions summary</h3>
            <p className="text-xs text-slate-500 mt-0.5">Per-employee monthly compliance deductions</p>
          </div>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading…</div>
        ) : employees.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">No active employees found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">PF (12%)</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">ESI (0.75%)</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">PT ({ptState})</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Total / Month</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map(emp => {
                  const basic = emp.salaryStructure?.basic || 0;
                  const gross = basic + (emp.salaryStructure?.hra || 0) + (emp.salaryStructure?.allowances || 0);
                  const pf    = emp.pfApplicable  ? Math.round(basic * 0.12)  : 0;
                  const esi   = emp.esiApplicable && gross < 21000 ? Math.round(gross * 0.0075) : 0;
                  const pt    = (emp.ptApplicable ? getPTDeduction(ptState, gross) : 0);
                  const ttl   = pf + esi + pt;

                  return (
                    <tr key={emp._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-3.5">
                        <div className="text-sm font-medium text-slate-900">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.department}</div>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Switch
                          checked={!!emp.pfApplicable}
                          onCheckedChange={(val) => pfMut.mutate({ id: emp._id, val })}
                          disabled={pfMut.isPending}
                        />
                        <div className="text-[10px] mt-1 font-mono text-slate-500">{pf ? formatINR(pf) : '—'}</div>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Switch
                          checked={!!emp.esiApplicable}
                          onCheckedChange={(val) => esiMut.mutate({ id: emp._id, val })}
                          disabled={esiMut.isPending}
                        />
                        <div className="text-[10px] mt-1 font-mono text-slate-500">{esi ? formatINR(esi) : '—'}</div>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Switch
                          checked={!!emp.ptApplicable}
                          onCheckedChange={(val) => ptMut.mutate({ id: emp._id, val })}
                          disabled={ptMut.isPending}
                        />
                        <div className="text-[10px] mt-1 font-mono text-slate-500">{pt ? formatINR(pt) : '—'}</div>
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right font-mono font-semibold text-red-700">−{formatINR(ttl)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}